import {noop} from '@benchling/js-utils';
import {createStyles, Group} from '@mantine/core';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import PlateMapContainer from 'notebook/integrations/plate-maps/PlateMapContainer';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import useWellSchemaLayers from 'samples/plate-maps/hooks/useWellSchemaLayers';
import {DisplayLayerOptionSection} from 'samples/plate-maps/PlateMapConstants';
import PlateMapSummary from 'samples/plate-maps/PlateMapSummary';
import {ScratchPlateDesignState} from 'samples/plate-maps/scratch-plates/ScratchPlateDesignModalUtil';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import OverflowMenu from 'ui-library/menus/OverflowMenu';

import SaveScratchPlateAsDesignTemplateModal from './SaveScratchPlateAsDesignTemplateModal';
import {useScratchPlateData, useWellSchemaOptionsForPlateMap} from './ScratchPlateDesignSummaryUtil';

export interface ScratchPlateDesignSummaryProps {
  scratchPlateId: string;
  locked: boolean;
  viewer: boolean;
  onEdit: (initialState: ScratchPlateDesignState, scratchPlateId: string, refetch: () => void) => void;
  onFillPlates?: (initialDesignState: ScratchPlateDesignState) => void;
}

const useStyles = createStyles(() => ({
  errorBannerWrapper: {
    alignSelf: 'flex-start',
  },
}));

export default function ScratchPlateDesignSummary({
  scratchPlateId,
  locked,
  viewer,
  onEdit,
  onFillPlates,
}: ScratchPlateDesignSummaryProps): JSX.Element {
  const {classes} = useStyles();
  const {plate, plateMap, containerSchemaId, dataFrameIsInaccessible, refetch} =
    useScratchPlateData(scratchPlateId);
  const [saveAsDesignModalOpen, setSaveAsDesignModalOpen] = useState(false);

  const wellSchemaLayers = useWellSchemaLayers(containerSchemaId, noop, []);
  const wellSchemaOptions = useWellSchemaOptionsForPlateMap(plateMap, wellSchemaLayers);

  const isDisabled = !BenchlingConfig.ENABLE_SCRATCH_PLATES || locked || plate == null || plateMap == null;

  function buildDesignState(): ScratchPlateDesignState {
    const {name, height, width, plateSchemaId} = plate!;
    const plateSchema = plateSchemaId != null ? SampleSchemaStore.getPlateSchema(plateSchemaId) : null;
    return {name, plateSize: [height, width], plateSchema, plateMap: plateMap!};
  }

  const headerControls = !viewer ? (
    <Group spacing={8} noWrap={true}>
      <Button
        size={ButtonSize.MINI}
        icon="pen"
        aria-label="Edit plate design"
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        disabled={isDisabled}
        onClick={() => {
          if (plate == null || plateMap == null) {
            return;
          }
          const {name, height, width, plateSchemaId} = plate;
          const plateSchema = plateSchemaId != null ? SampleSchemaStore.getPlateSchema(plateSchemaId) : null;
          onEdit(
            {
              name,
              plateSize: [height, width],
              plateSchema,
              plateMap,
            },
            scratchPlateId,
            refetch
          );
        }}
      />
      {BenchlingConfig.ENABLE_SCRATCH_PLATES && (
        <OverflowMenu
          enableTooltip={false}
          buttonProps={{'aria-label': 'More options', className: undefined, disabled: isDisabled}}
          menuProps={{dropLeft: true}}
          options={[
            {
              contents: <>Save as template</>,
              onSelect: () => {
                setSaveAsDesignModalOpen(true);
              },
              isDisabled: plate == null,
            },
          ]}
        />
      )}
      {onFillPlates != null && (
        <Button
          size={ButtonSize.MINI}
          icon="fill-grid"
          text="Fill plates"
          buttonStyle={ButtonStyle.PRIMARY}
          disabled={isDisabled}
          onClick={() => {
            onFillPlates(buildDesignState());
          }}
        />
      )}
    </Group>
  ) : null;

  return (
    <>
      <PlateMapContainer
        clientStateId={`scratch-plate-${scratchPlateId}`}
        header="Plate design"
        headerControls={headerControls}
        subheader={plate?.name ?? scratchPlateId}
        onHeaderContextMenu={noop}
        plateMapType={PlateMapType.DESIGN}
      >
        {dataFrameIsInaccessible ? (
          <div className={classes.errorBannerWrapper}>
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text="You do not have access to this plate design."
            />
          </div>
        ) : plateMap != null ? (
          <PlateMapSummary
            plateMap={plateMap}
            plateDataParametersOptions={[]}
            wellSchemaOptions={wellSchemaOptions}
            frozenReagentAssignmentGroups={null}
            procedureStudyDesignType={null}
            plateMapType={PlateMapType.DESIGN}
            hiddenSections={[DisplayLayerOptionSection.CONTENTS]}
            additionalMetadataContent={<></>}
          />
        ) : (
          <CenteredHorizontalThrobber />
        )}
      </PlateMapContainer>
      {saveAsDesignModalOpen && (
        <SaveScratchPlateAsDesignTemplateModal
          scratchPlateId={scratchPlateId}
          defaultName={plate?.name ?? ''}
          opened={saveAsDesignModalOpen}
          onClose={() => {
            setSaveAsDesignModalOpen(false);
          }}
        />
      )}
    </>
  );
}
